import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SALON_PHOTOS, SALON_INFO } from "../data/assets";

export const Reviews = () => {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const reviews = t.reviews.list;

  const prev = () => setIdx((p) => (p - 1 + reviews.length) % reviews.length);
  const next = () => setIdx((p) => (p + 1) % reviews.length);

  return (
    <section
      id="reviews"
      className="relative py-24 md:py-36 overflow-hidden"
      data-testid="reviews-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${SALON_PHOTOS.reviews})` }}
        />
        <div className="absolute inset-0 bg-[#050505]/85" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
              {t.reviews.eyebrow}
            </span>
            <span className="h-px w-10 bg-[#C5A059]" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
            {t.reviews.title}
          </h2>

          {/* Rating */}
          <div className="mt-8 inline-flex items-center gap-3 px-6 py-3 border border-white/10 bg-[#050505]/50 backdrop-blur-md">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="text-[#C5A059] fill-[#C5A059]"
                  strokeWidth={1}
                />
              ))}
            </div>
            <span className="text-sm text-white/80 font-light tracking-wide">
              {t.reviews.rating}
            </span>
          </div>
        </motion.div>

        {/* Slider */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="relative max-w-4xl mx-auto"
          data-testid="reviews-slider"
        >
          <Quote
            size={120}
            className="absolute -top-10 left-0 text-[#C5A059]/10"
            strokeWidth={1}
          />

          <div className="relative min-h-[280px] md:min-h-[240px] flex items-center">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="text-center px-4 md:px-12"
                data-testid={`review-card-${idx}`}
              >
                <p className="font-display italic text-2xl md:text-3xl lg:text-4xl text-white leading-[1.4] mb-8">
                  „{reviews[idx].text}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  <span className="h-px w-8 bg-[#C5A059]" />
                  <cite className="not-italic text-sm uppercase tracking-[0.25em] text-[#C5A059]">
                    {reviews[idx].author}
                  </cite>
                  <span className="h-px w-8 bg-[#C5A059]" />
                </div>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-center gap-6">
            <button
              onClick={prev}
              className="w-12 h-12 rounded-full border border-white/15 hover:border-[#C5A059] text-white hover:text-[#C5A059] flex items-center justify-center transition-all"
              data-testid="review-prev-btn"
              aria-label="Predchádzajúca recenzia"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>

            <div className="flex gap-2" data-testid="review-dots">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-px transition-all duration-500 ${
                    i === idx ? "w-12 bg-[#C5A059]" : "w-6 bg-white/20 hover:bg-white/40"
                  }`}
                  data-testid={`review-dot-${i}`}
                  aria-label={`Recenzia ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-12 h-12 rounded-full border border-white/15 hover:border-[#C5A059] text-white hover:text-[#C5A059] flex items-center justify-center transition-all"
              data-testid="review-next-btn"
              aria-label="Ďalšia recenzia"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* See all on Google */}
          <div className="mt-12 text-center">
            <a
              href={SALON_INFO.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C5A059] transition-colors border-b border-white/15 hover:border-[#C5A059] pb-1"
              data-testid="reviews-google-link"
            >
              {t.reviews.see_all} →
            </a>
            <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-white/30">
              Google · {SALON_INFO.reviews} recenzií · {SALON_INFO.rating} ★
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
